import { Fragment, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { PageNavbar } from '@/pages/account';
import { useLayout } from '@/providers';
import { useSnackbar } from 'notistack';
import {
  useGetPageQuery,
  useCreatePageMutation,
  useUpdateContentMutation,
} from '@/services/CmsService';
import {
  languageOptions,
  Page,
  PageType,
  sectionData,
} from '@/types/cms.types';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { setLanguage } from '@/store/authSlice';
import { ErrorApiResponse } from '@/types/api.types';
import { usePermissions } from '@/hooks/usePermissions';

// Fixed, non-editable identifier for the single content section this page
// manages. The public site only reads the description text, not the title.
const MARKETING_DESCRIPTION_TITLE = 'Marketing Description';
const DESCRIPTION_MAX_LENGTH = 500;

const findMarketingSection = (
  pageData: { data?: Page[] } | undefined
): { pageId: string | null; section: sectionData | null } => {
  const page = pageData?.data?.find(
    (p: Page) => p.pageType === PageType.COOKIE_CONSENT
  );
  const content = (page?.content ?? []) as unknown as sectionData[];
  return { pageId: page?.id ?? null, section: content[0] ?? null };
};

const CookieConsent = () => {
  const { currentLayout } = useLayout();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const language = useSelector((state: RootState) => state.auth.language);
  const { hasPermission } = usePermissions();

  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [pageId, setPageId] = useState<string | null>(null);
  const [contentId, setContentId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: pageData,
    isLoading,
    error: fetchError,
  } = useGetPageQuery({ pageType: PageType.COOKIE_CONSENT, language });
  const [createPage] = useCreatePageMutation();
  const [updateContent] = useUpdateContentMutation();

  useEffect(() => {
    const { pageId: id, section } = findMarketingSection(pageData);
    setPageId(id);
    setDescription(section?.content ?? '');
    setContentId(section?.id ?? null);
  }, [pageData]);

  useEffect(() => {
    if (fetchError) {
      enqueueSnackbar('Failed to fetch Cookie Consent Settings', {
        variant: 'error',
      });
    }
  }, [fetchError, enqueueSnackbar]);

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(setLanguage(e.target.value));
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setDescription(e.target.value);
    if (error) setError('');
  };

  const handleCancel = () => {
    const { section } = findMarketingSection(pageData);
    setDescription(section?.content ?? '');
    setError('');
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      setError('Description is required');
      enqueueSnackbar('Description is required', { variant: 'error' });
      return;
    }

    setIsSubmitting(true);
    try {
      if (contentId && pageId) {
        await updateContent({
          pageId,
          contentId,
          body: {
            title: MARKETING_DESCRIPTION_TITLE,
            content: description,
            display_order: 1,
          },
        }).unwrap();
      } else {
        await createPage({
          pageType: PageType.COOKIE_CONSENT,
          pageId,
          content: [
            {
              title: MARKETING_DESCRIPTION_TITLE,
              content: description,
              display_order: 1,
            },
          ],
        }).unwrap();
      }
      enqueueSnackbar('Cookie Consent Settings saved successfully', {
        variant: 'success',
      });
      setError('');
    } catch (err) {
      enqueueSnackbar(
        (err as ErrorApiResponse).data?.message ||
          'Failed to save Cookie Consent Settings',
        { variant: 'error' }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="card card-grid min-w-full">
        <div className="card-body">
          <div className="text-center py-4" role="status" aria-live="polite">
            Loading Cookie Consent Settings...
          </div>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="card card-grid min-w-full">
        <div className="card-body">
          <div
            className="text-center py-4 text-red-500"
            role="alert"
            aria-live="polite"
          >
            Failed to load Cookie Consent Settings. Please try again.
          </div>
        </div>
      </div>
    );
  }

  if (!hasPermission('cms_management_read')) {
    return (
      <Fragment>
        <PageNavbar />
        {currentLayout?.name === 'demo1-layout' && (
          <Container>
            <Toolbar>
              <ToolbarHeading>
                <ToolbarPageTitle id="cookie-consent-heading" />
                <ToolbarDescription>
                  Manage the cookie consent categories description
                </ToolbarDescription>
              </ToolbarHeading>
            </Toolbar>
            <div className="bg-gray-100 p-6 rounded-lg shadow-lg flex flex-col">
              <div className="text-center py-4 text-gray-500">
                You don't have permission to view Cookie Consent Settings
                content.
              </div>
            </div>
          </Container>
        )}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <PageNavbar />
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle id="cookie-consent-heading" />
              <ToolbarDescription>
                Manage the marketing category description
              </ToolbarDescription>
            </ToolbarHeading>
            <div className="relative">
              <select
                id="language-select"
                value={language}
                onChange={handleLanguageChange}
                className="select select-bordered pl-8 pr-8 h-10 leading-normal"
                aria-label="Select language"
              >
                {languageOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <img
                src="/media/flags/globe.svg"
                alt="Globe"
                className="absolute dark:brightness-0 dark:invert contrast:brightness-0 left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 invert pointer-events-none"
                aria-hidden="true"
              />
            </div>
          </Toolbar>
          <div className="bg-gray-100 p-6 rounded-lg shadow-lg flex flex-col">
            <div className="flex-1">
              <div>
                <label
                  htmlFor="marketing-description"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Marketing Description
                </label>
                <textarea
                  id="marketing-description"
                  name="description"
                  value={description}
                  onChange={handleDescriptionChange}
                  maxLength={DESCRIPTION_MAX_LENGTH}
                  rows={6}
                  className={clsx(
                    'input input-bordered w-full resize-none h-24 p-3',
                    error && 'border-red-500 focus:ring-red-500'
                  )}
                  disabled={isSubmitting}
                  autoComplete="off"
                  aria-invalid={!!error}
                  aria-describedby={
                    error ? 'marketing-description-error' : undefined
                  }
                  placeholder={`Enter description (max ${DESCRIPTION_MAX_LENGTH} characters)`}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {description.length}/{DESCRIPTION_MAX_LENGTH} characters
                </p>
                {error && (
                  <p
                    id="marketing-description-error"
                    className="text-red-500 text-sm mt-1"
                  >
                    {error}
                  </p>
                )}
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCancel}
                className="btn btn-sm btn-light"
                aria-label="Cancel"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              {hasPermission('cms_management_update') && (
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="btn btn-sm btn-primary"
                  aria-label="Save Changes"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>
        </Container>
      )}
    </Fragment>
  );
};

export { CookieConsent };
